import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';

const STEPS = [
  { key: 'welcome', label: 'Session Info', icon: 'info' },
  { key: 'camera', label: 'Camera Check', icon: 'videocam' },
  { key: 'mic', label: 'Mic Check', icon: 'mic' },
];

const AITrainerSetupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const trainingData = location.state;

  // ── Step state ──
  const [currentStep, setCurrentStep] = useState(0);

  // ── Camera state ──
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [micDevices, setMicDevices] = useState([]);
  const [selectedMic, setSelectedMic] = useState('');
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  // ── Mic state ──
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const animationRef = useRef(null);
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [micError, setMicError] = useState('');
  const [transcript, setTranscript] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingCountdown, setRecordingCountdown] = useState(0);

  const testPhrase = "Hello, I'm ready to start my AI interview training session.";
  const candidateName = trainingData?.candidateName || 'Candidate';

  // ── Redirect if no data ──
  useEffect(() => {
    if (!trainingData) {
      navigate('/ai-trainer');
    }
  }, [trainingData, navigate]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      stopCamera();
      stopMicListening();
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    };
  }, []);

  // ═══════════════════════════════════════
  // CAMERA LOGIC
  // ═══════════════════════════════════════
  const initializeDevices = useCallback(async () => {
    setDevicesLoading(true);
    setCameraError('');
    try {
      const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      const microphones = devices.filter(d => d.kind === 'audioinput');
      setCameraDevices(cameras);
      setMicDevices(microphones);
      if (cameras.length > 0) setSelectedCamera(cameras[0].deviceId);
      if (microphones.length > 0) setSelectedMic(microphones[0].deviceId);
      tempStream.getTracks().forEach(t => t.stop());
    } catch (error) {
      setCameraError('Please allow camera and microphone access to continue.');
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  // Initialize devices when entering camera step
  useEffect(() => {
    if (currentStep === 1 && cameraDevices.length === 0 && !devicesLoading) {
      initializeDevices();
    }
  }, [currentStep, cameraDevices.length, devicesLoading, initializeDevices]);

  // Start camera when device selected
  useEffect(() => {
    if (currentStep === 1 && selectedCamera && !devicesLoading) {
      startCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCamera, currentStep]);

  const startCamera = async () => {
    setCameraError('');
    setCameraReady(false);
    try {
      stopCamera();
      const constraints = {
        video: selectedCamera
          ? { deviceId: { exact: selectedCamera }, width: { ideal: 1280 }, height: { ideal: 720 } }
          : { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => setCameraReady(true);
        try { await videoRef.current.play(); } catch (e) {}
      }
    } catch (error) {
      if (error.name === 'NotAllowedError') setCameraError('Camera access denied. Please allow access.');
      else if (error.name === 'NotFoundError') setCameraError('No camera found. Please connect a camera.');
      else setCameraError(`Unable to access camera: ${error.message}`);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
    setCameraReady(false);
  };

  // Stop camera when leaving camera step
  useEffect(() => {
    if (currentStep !== 1) stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // ═══════════════════════════════════════
  // MIC LOGIC
  // ═══════════════════════════════════════
  const startMicListening = useCallback(async () => {
    setMicError('');
    try {
      stopMicListening();
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedMic ? { exact: selectedMic } : undefined,
          echoCancellation: true, noiseSuppression: true, autoGainControl: true
        }
      });
      micStreamRef.current = micStream;
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(micStream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      setIsListening(true);
      monitorAudioLevel();
    } catch (error) {
      if (error.name === 'NotAllowedError') setMicError('Microphone access denied.');
      else if (error.name === 'NotFoundError') setMicError('No microphone found.');
      else setMicError('Unable to access microphone.');
    }
  }, [selectedMic]);

  const stopMicListening = () => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setIsListening(false);
    setAudioLevel(0);
  };

  const monitorAudioLevel = () => {
    if (!analyserRef.current) return;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    const checkLevel = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(Math.min(100, (average / 128) * 100));
      animationRef.current = requestAnimationFrame(checkLevel);
    };
    checkLevel();
  };

  // Initialize mic when entering mic step
  useEffect(() => {
    if (currentStep === 2) {
      // Init speech recognition
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SR();
        recognitionRef.current.lang = 'en-US';
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = true;
      }
      if (selectedMic) startMicListening();
    } else {
      stopMicListening();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedMic]);

  const handleStartRecording = () => {
    if (!recognitionRef.current) {
      setTranscript('Speech recognition not available. You can still continue.');
      return;
    }
    setIsRecording(true);
    setTranscript('');
    setRecordingCountdown(5);

    const countdownInterval = setInterval(() => {
      setRecordingCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownInterval); return 0; }
        return prev - 1;
      });
    }, 1000);

    recognitionRef.current.onresult = (event) => {
      let text = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        text += event.results[i][0].transcript;
      }
      setTranscript(text);
    };
    recognitionRef.current.onend = () => { setIsRecording(false); setRecordingCountdown(0); clearInterval(countdownInterval); };
    recognitionRef.current.onerror = (event) => {
      setIsRecording(false); setRecordingCountdown(0); clearInterval(countdownInterval);
      if (event.error === 'not-allowed') setTranscript('Microphone access denied.');
      else setTranscript('Speech test completed. You can continue.');
    };

    try { recognitionRef.current.start(); } catch (e) {
      setIsRecording(false); setRecordingCountdown(0); clearInterval(countdownInterval);
      setTranscript('Speech test completed. You can continue.');
    }

    setTimeout(() => {
      try { recognitionRef.current?.stop(); } catch (e) {}
    }, 5000);
  };

  // ═══════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════
  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
    else navigate('/ai-trainer');
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep(currentStep + 1);
  };

  const handleStartSession = () => {
    stopCamera();
    stopMicListening();
    const completeData = {
      ...trainingData,
      cameraDeviceId: selectedCamera,
      micDeviceId: selectedMic
    };
    navigate('/ai-trainer/session', { state: completeData });
  };

  if (!trainingData) return null;

  // ═══════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════
  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col overflow-x-hidden bg-gray-50 dark:bg-gray-900">
      <Header variant="default" />

      <main className="flex-1 flex flex-col">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-4xl">

          {/* ── Back button ── */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-text-light-secondary dark:text-text-dark-secondary hover:text-primary mb-4 transition-colors"
          >
            <Icon name="arrow_back" size={20} />
            <span>{currentStep > 0 ? 'Back' : 'Back to AI Trainer'}</span>
          </button>

          {/* ── Stepper ── */}
          <div className="flex items-center justify-center gap-1 mb-6">
            {STEPS.map((step, idx) => (
              <div key={step.key} className="flex items-center">
                <button
                  onClick={() => idx <= currentStep && setCurrentStep(idx)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    idx === currentStep
                      ? 'bg-primary text-white shadow-lg shadow-primary/25'
                      : idx < currentStep
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 cursor-pointer hover:bg-green-200'
                      : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                  }`}
                  disabled={idx > currentStep}
                >
                  {idx < currentStep ? (
                    <Icon name="check_circle" size={18} />
                  ) : (
                    <Icon name={step.icon} size={18} />
                  )}
                  <span className="hidden sm:inline">{step.label}</span>
                  <span className="sm:hidden">{idx + 1}</span>
                </button>
                {idx < STEPS.length - 1 && (
                  <div className={`w-8 h-0.5 mx-1 ${idx < currentStep ? 'bg-green-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
                )}
              </div>
            ))}
          </div>

          {/* ── Session info bar ── */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-4 py-3 mb-6 border border-blue-200 dark:border-blue-800 flex items-center gap-3 text-sm">
            <Icon name="psychology" size={18} className="text-blue-600 flex-shrink-0" />
            <span className="text-blue-700 dark:text-blue-300">
              {trainingData?.jobTitle || 'General Training'} &bull; {trainingData?.numQuestions || 5} questions &bull; {trainingData?.difficultyLevel || 'Medium'}
            </span>
          </div>

          {/* ════════════════════════════════════ */}
          {/* STEP 1: WELCOME / SESSION INFO      */}
          {/* ════════════════════════════════════ */}
          {currentStep === 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-2">
                  AI Interview Training
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Hi {candidateName}, welcome to your practice session!
                </p>

                {/* How it works */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {[
                    { icon: 'record_voice_over', text: 'AI speaks questions aloud', color: 'text-blue-500' },
                    { icon: 'mic', text: 'You respond with your voice', color: 'text-green-500' },
                    { icon: 'psychology', text: 'Instant AI feedback per answer', color: 'text-purple-500' },
                    { icon: 'assessment', text: 'Scores & grades (A+ to F)', color: 'text-amber-500' },
                    { icon: 'lightbulb', text: 'Improvement suggestions', color: 'text-orange-500' },
                    { icon: 'email', text: 'PDF report sent to your email', color: 'text-pink-500' },
                  ].map(({ icon, text, color }, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600">
                      <Icon name={icon} size={20} className={color} />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{text}</span>
                    </div>
                  ))}
                </div>

                {/* Practice note */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6 border border-green-200 dark:border-green-800">
                  <div className="flex items-start gap-3">
                    <Icon name="school" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-green-700 dark:text-green-300">
                      <p className="font-semibold mb-1">This is Practice — Not a Real Interview</p>
                      <p>No pressure! Focus on learning from AI feedback. You can take as many sessions as you want. All reports are saved for progress tracking.</p>
                    </div>
                  </div>
                </div>

                {/* Tips */}
                <div className="flex items-start gap-3 text-sm text-gray-500 dark:text-gray-400 mb-6">
                  <Icon name="tips_and_updates" size={18} className="mt-0.5 flex-shrink-0" />
                  <span>Ensure a quiet environment and stable internet connection for the best experience.</span>
                </div>

                <div className="flex justify-end">
                  <Button size="lg" onClick={handleNext} className="px-8">
                    <span>Next: Camera Check</span>
                    <Icon name="arrow_forward" size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════ */}
          {/* STEP 2: CAMERA CHECK                */}
          {/* ════════════════════════════════════ */}
          {currentStep === 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 md:p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  Camera Check
                </h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-5">
                  Position yourself clearly in the frame with good lighting.
                </p>

                {/* Device selectors */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Camera</label>
                    <select
                      value={selectedCamera}
                      onChange={e => setSelectedCamera(e.target.value)}
                      className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                      disabled={cameraDevices.length === 0}
                    >
                      {cameraDevices.length === 0 ? (
                        <option>No camera detected</option>
                      ) : (
                        cameraDevices.map((d, i) => (
                          <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Microphone</label>
                    <select
                      value={selectedMic}
                      onChange={e => setSelectedMic(e.target.value)}
                      className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                      disabled={micDevices.length === 0}
                    >
                      {micDevices.length === 0 ? (
                        <option>No microphone detected</option>
                      ) : (
                        micDevices.map((d, i) => (
                          <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* Video preview */}
                <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-video flex items-center justify-center mb-5 border border-gray-200 dark:border-gray-600">
                  {devicesLoading ? (
                    <div className="text-center text-gray-400">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
                      <p className="text-sm">Loading camera...</p>
                    </div>
                  ) : cameraError ? (
                    <div className="text-center p-6">
                      <Icon name="videocam_off" size={40} className="text-red-400 mx-auto mb-3" />
                      <p className="text-red-400 text-sm mb-3">{cameraError}</p>
                      <Button size="sm" variant="outline" onClick={initializeDevices}>Retry</Button>
                    </div>
                  ) : (
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                  )}
                </div>

                {/* Camera tips */}
                {!cameraError && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 mb-5 border border-yellow-200 dark:border-yellow-800 text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Tips: </span>
                    Center yourself, ensure good lighting, avoid backlighting, keep camera at eye level.
                  </div>
                )}

                <div className="flex justify-between">
                  <Button size="lg" variant="outline" onClick={handleBack}>
                    <Icon name="arrow_back" size={18} className="mr-1" /> Back
                  </Button>
                  <Button size="lg" onClick={handleNext} disabled={!!cameraError || devicesLoading} className="px-8">
                    <span>Next: Mic Check</span>
                    <Icon name="arrow_forward" size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════ */}
          {/* STEP 3: MIC CHECK                   */}
          {/* ════════════════════════════════════ */}
          {currentStep === 2 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 md:p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  Microphone Check
                </h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-5">
                  Test your mic by reading the phrase below. The AI uses speech recognition to understand your answers.
                </p>

                {/* Mic selector */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Microphone</label>
                  <select
                    value={selectedMic}
                    onChange={e => setSelectedMic(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    disabled={micDevices.length === 0}
                  >
                    {micDevices.length === 0 ? (
                      <option>No microphone detected</option>
                    ) : (
                      micDevices.map((d, i) => (
                        <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
                      ))
                    )}
                  </select>
                </div>

                {micError ? (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-5 mb-5">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon name="mic_off" size={28} className="text-red-500" />
                      <p className="text-red-600 dark:text-red-400 text-sm">{micError}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={startMicListening}>Retry</Button>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-5 mb-5">
                    {/* Test phrase */}
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 uppercase font-semibold tracking-wide">Read this phrase:</p>
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800">
                        <p className="text-base font-semibold text-gray-800 dark:text-gray-200">"{testPhrase}"</p>
                      </div>
                    </div>

                    {/* Audio level */}
                    <div className="flex items-center gap-3 mb-4">
                      <Icon name={isListening ? 'mic' : 'mic_off'} size={22} className={isListening ? 'text-green-500' : 'text-gray-400'} />
                      <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 overflow-hidden">
                        <div className="bg-green-500 h-full transition-all duration-100" style={{ width: `${audioLevel}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 w-16 text-right">
                        {isListening ? `${Math.round(audioLevel)}%` : 'Off'}
                      </span>
                    </div>

                    {/* Record button */}
                    <Button onClick={handleStartRecording} disabled={!isListening || isRecording} className="mb-4">
                      {isRecording ? (
                        <><div className="animate-pulse w-2.5 h-2.5 bg-red-500 rounded-full mr-2"></div>Recording... {recordingCountdown}s</>
                      ) : (
                        <><Icon name="mic" size={16} className="mr-2" />Test Speech Recognition</>
                      )}
                    </Button>

                    {/* Transcript result */}
                    {transcript && (
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">What I heard:</p>
                        <p className="text-sm text-gray-800 dark:text-gray-200">{transcript}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Mic tips */}
                {!micError && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 mb-5 border border-yellow-200 dark:border-yellow-800 text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Tips: </span>
                    Speak clearly at normal volume, keep mic 6-12 inches away, minimize background noise.
                  </div>
                )}

                <div className="flex justify-between">
                  <Button size="lg" variant="outline" onClick={handleBack}>
                    <Icon name="arrow_back" size={18} className="mr-1" /> Back
                  </Button>
                  <Button
                    size="lg"
                    onClick={handleStartSession}
                    disabled={!!micError || !isListening}
                    className="px-8 bg-green-600 hover:bg-green-700"
                  >
                    <Icon name="play_arrow" size={20} className="mr-2" />
                    Start Training Session
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default AITrainerSetupPage;
